import EmblaCarousel from "./embla-carousel.esm.js";
import AutoScroll from "./embla-carousel-auto-scroll.esm.js";

const clampInt = (value, min, max) =>
  Math.min(max, Math.max(min, Math.round(Number(value)) || min));

const readBp = (viewport, spvKey, spgKey, spvFallback, spgFallback) => {
  const spv = clampInt(viewport.dataset[spvKey], 1, 12) || spvFallback;
  const spgRaw = clampInt(viewport.dataset[spgKey], 1, 12) || spgFallback;
  const spg = Math.min(spgRaw, Math.max(1, Math.floor(spv)));
  return { spv, spg };
};

const currentBreakpoint = (viewport) => {
  const xs = readBp(viewport, "sliderSpvXs", "sliderSpgXs", 1, 1);
  const sm = readBp(viewport, "sliderSpvSm", "sliderSpgSm", 2, 1);
  const md = readBp(viewport, "sliderSpvMd", "sliderSpgMd", 3, 1);
  const lg = readBp(viewport, "sliderSpvLg", "sliderSpgLg", 7, 1);
  const xl = readBp(viewport, "sliderSpvXl", "sliderSpgXl", 9, 1);

  const w =
    typeof window !== "undefined"
      ? window.innerWidth || viewport.clientWidth
      : 990;
  if (w >= 1360) return xl;
  if (w >= 1200) return lg;
  if (w >= 990) return md;
  if (w >= 576) return sm;
  return xs;
};

const peakSlidesPerView = (viewport) => {
  const xs = readBp(viewport, "sliderSpvXs", "sliderSpgXs", 1, 1);
  const sm = readBp(viewport, "sliderSpvSm", "sliderSpgSm", 2, 1);
  const md = readBp(viewport, "sliderSpvMd", "sliderSpgMd", 3, 1);
  const lg = readBp(viewport, "sliderSpvLg", "sliderSpgLg", 7, 1);
  const xl = readBp(viewport, "sliderSpvXl", "sliderSpgXl", 9, 1);
  return Math.max(xs.spv, sm.spv, md.spv, lg.spv, xl.spv);
};

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const autoScrollSpeedFromTheme = (viewport) => {
  const sliderSpeed = Math.max(200, Number(viewport.dataset.speed) || 1000);
  return Math.min(8, Math.max(0.5, 4500 / sliderSpeed));
};

const debounce = (fn, ms) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
};

const destroyBrandsSlider = (viewport) => {
  if (!viewport) return;
  viewport._brandsResizeCleanup?.();
  viewport._brandsResizeCleanup = null;
  if (viewport._brandsEmbla) {
    viewport._brandsEmbla.destroy();
    viewport._brandsEmbla = null;
  }
};

const buildEmblaOptions = (viewport) => {
  const { spv, spg } = currentBreakpoint(viewport);
  viewport.style.setProperty("--brands-spv", String(spv));

  const slideCount = viewport.querySelectorAll(".brands__slide").length;
  const loopRequested = viewport.dataset.loop === "true";
  const peak = peakSlidesPerView(viewport);
  const loopEnabled = loopRequested && slideCount >= peak * 2;

  const direction =
    document.documentElement.getAttribute("dir") === "rtl" ? "rtl" : "ltr";

  return {
    axis: "x",
    direction,
    loop: loopEnabled,
    align: "start",
    slidesToScroll: spg,
    containScroll: loopEnabled ? false : "trimSnaps",
    dragFree: false,
    watchDrag: true,
    watchResize: true,
    skipSnaps: false,
  };
};

const buildPlugins = (viewport, reducedMotion) => {
  const plugins = [];
  const autoplayOn = viewport.dataset.autoplay === "true" && !reducedMotion;
  if (!autoplayOn) return plugins;

  const delaySec = Math.max(0, Number(viewport.dataset.autoplayDelay) || 0);

  plugins.push(
    AutoScroll({
      speed: autoScrollSpeedFromTheme(viewport),
      startDelay: delaySec * 1000,
      stopOnMouseEnter: true,
      stopOnInteraction: false,
      stopOnFocusIn: true,
      playOnInit: true,
      direction: "forward",
    }),
  );
  return plugins;
};

const wireNav = (viewport, embla) => {
  const wrap = viewport.closest(".brands__slider-wrap");
  const prevEl = wrap?.querySelector(".brands__slider-button--prev");
  const nextEl = wrap?.querySelector(".brands__slider-button--next");
  if (!prevEl || !nextEl) return undefined;

  const syncDisabled = () => {
    const prev = !embla.canScrollPrev();
    const next = !embla.canScrollNext();
    prevEl.disabled = prev;
    nextEl.disabled = next;
    prevEl.classList.toggle("swiper-button-disabled", prev);
    nextEl.classList.toggle("swiper-button-disabled", next);
    prevEl.setAttribute("aria-disabled", prev ? "true" : "false");
    nextEl.setAttribute("aria-disabled", next ? "true" : "false");
  };

  const onPrev = () => embla.scrollPrev();
  const onNext = () => embla.scrollNext();

  prevEl.addEventListener("click", onPrev);
  nextEl.addEventListener("click", onNext);
  embla.on("select", syncDisabled);
  embla.on("reInit", syncDisabled);
  syncDisabled();

  return () => {
    prevEl.removeEventListener("click", onPrev);
    nextEl.removeEventListener("click", onNext);
    embla.off("select", syncDisabled);
    embla.off("reInit", syncDisabled);
  };
};

const initBrandsSliders = (root) => {
  const scope = root && root.querySelectorAll ? root : document;
  const viewports = scope.querySelectorAll(".js-brands-slider");

  viewports.forEach((viewport) => {
    destroyBrandsSlider(viewport);
    if (viewport.querySelectorAll(".brands__slide").length < 1) return;

    const rm = prefersReducedMotion();
    const options = buildEmblaOptions(viewport);
    const plugins = buildPlugins(viewport, rm);

    const embla = EmblaCarousel(viewport, options, plugins);
    viewport._brandsEmbla = embla;

    const teardownNav = wireNav(viewport, embla);

    const reapply = debounce(() => {
      const nextRm = prefersReducedMotion();
      embla.reInit(
        buildEmblaOptions(viewport),
        buildPlugins(viewport, nextRm),
      );
    }, 150);

    window.addEventListener("resize", reapply);
    viewport._brandsResizeCleanup = () => {
      window.removeEventListener("resize", reapply);
      teardownNav?.();
    };
  });
};

const brandsSectionFromEvent = (event) => {
  const t = event.target;
  if (!t || !t.classList) return null;
  if (t.classList.contains("brands-section")) return t;
  return t.closest ? t.closest(".brands-section") : null;
};

initBrandsSliders(document);

const reinits = [
  "shopify:section:load",
  "shopify:section:select",
  "shopify:section:reorder",
  "shopify:block:select",
  "shopify:block:deselect",
];

reinits.forEach((type) => {
  document.addEventListener(type, function (event) {
    const section = brandsSectionFromEvent(event);
    if (!section) return;
    const run = () => initBrandsSliders(section);
    if (
      type === "shopify:block:select" ||
      type === "shopify:block:deselect"
    ) {
      setTimeout(run, 0);
    } else {
      run();
    }
  });
});

document.addEventListener("shopify:section:unload", function (event) {
  const section = brandsSectionFromEvent(event);
  if (!section) return;
  section.querySelectorAll(".js-brands-slider").forEach((vp) => {
    destroyBrandsSlider(vp);
  });
});
